// First-time-at-cell tracking. Pure. No React, no Three.js. Tracks
// which cells a source has already "fired" for, so an
// on_enter_location / on_stop entry can gate to the FIRST time a
// qualifying unit reaches a cell.
//
// Two shapes, both stored as JSON-safe primitives on the card instance
// (already synced) so the gate survives the save / P2P / auth snapshot
// round-trip without the CARD_SET_FIELDS machinery:
//
//   single-cell. A boolean flag (`_firstVisitFired`). Vaults of Zul:
//                  "The first time an Avatar stops here." One cell, one
//                  shot.
//   per-cell. An array of "col,row" keys (`_firstVisitCells`).
//                  Wayfaring Pilgrim: "enters EACH corner of the realm
//                  for the first time." One shot per distinct cell.
//
// Read (hasFiredForCell) and write (markCellFired) are split so the
// caller can gate with a pure condition and only burn the flag when the
// effect actually resolves (an optional "may" that the player declines
// must NOT consume the first-time).

const SINGLE_FLAG = '_firstVisitFired';
const PER_CELL_LIST = '_firstVisitCells';

export function cellKey(cell) {
  return cell ? `${cell.col},${cell.row}` : null;
}

/**
 * Has this source already fired its first-visit entry for `cell`?
 * @param {object} card     the source card instance.
 * @param {object} cell     { col, row }. The cell being checked.
 * @param {boolean} perCell true → per-distinct-cell; false → single shot.
 */
export function hasFiredForCell(card, cell, perCell) {
  if (!card) return false;
  if (!perCell) return card[SINGLE_FLAG] === true;
  const key = cellKey(cell);
  if (!key) return false;
  return Array.isArray(card[PER_CELL_LIST]) && card[PER_CELL_LIST].includes(key);
}

/**
 * Record that this source has now fired for `cell`. Idempotent.
 */
export function markCellFired(card, cell, perCell) {
  if (!card) return;
  if (!perCell) {
    card[SINGLE_FLAG] = true;
    return;
  }
  const key = cellKey(cell);
  if (!key) return;
  if (!Array.isArray(card[PER_CELL_LIST])) card[PER_CELL_LIST] = [];
  if (!card[PER_CELL_LIST].includes(key)) card[PER_CELL_LIST].push(key);
}

// Corner classification now lives in the positional module (the single
// source of truth for board-position facts). Re-exported here so existing
// importers (conditions.js) keep their import path.
export { cornerCells, isCornerCell } from './positional';
